import { useState, useEffect } from "react";
import styles from "./CompanyPagesControl.module.css";
import {
  IconSearch, IconBuilding,
  IconCheck, IconX, IconEye, IconEyeOff,
  IconToggleLeft, IconToggleRight,
  IconLoader2, IconAlertTriangle,
} from "@tabler/icons-react";
import { ALL_PAGES, ALL_PAGE_KEYS, CONTROLLABLE_PAGES } from "../../../interface/Pagesconfig";
import { isAlwaysEnabled } from "../../../interface/Pagefeaturemapping";
import {
  loadEnabledPagesForCompany,
  saveEnabledPages,
} from "../../../utils/pagePermissions";

export { ALL_PAGES, ALL_PAGE_KEYS };

export default function CompanyPagesControl() {
  const [selectedCompany, setSelected]  = useState<any | null>(null);
  const [enabledPages, setEnabledPages] = useState<string[]>([]);
  const [saved, setSaved]              = useState(false);
  const [search, setSearch]            = useState("");

  const [loading, setLoading]          = useState(false);
  const [saving, setSaving]            = useState(false);
  const [loadError, setLoadError]      = useState<string | null>(null);
  const [saveError, setSaveError]      = useState<string | null>(null);

  useEffect(() => {
    const coStr = sessionStorage.getItem("selectedCompany");
    if (!coStr) return;
    try {
      const company = JSON.parse(coStr);
      setSelected(company);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (!selectedCompany?.id) return;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    loadEnabledPagesForCompany(selectedCompany.id, true)
      .then((enabled) => {
        if (cancelled) return;
        setEnabledPages(enabled);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setLoadError("An error occurred while loading pages. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCompany?.id]);

  const toggle = (key: string) => {
    if (isAlwaysEnabled(key)) return; 
    setEnabledPages((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
    setSaved(false);
  };

  const toggleGroup = (keys: string[]) => {
    const toggleable = keys.filter((k) => !isAlwaysEnabled(k));
    setEnabledPages((prev) => {
      const allOn = toggleable.every((k) => prev.includes(k));
      return allOn
        ? prev.filter((k) => !toggleable.includes(k))
        : [...new Set([...prev, ...toggleable])];
    });
    setSaved(false);
  };

  const enableAll  = () => { setEnabledPages([...ALL_PAGE_KEYS]); setSaved(false); };
  const disableAll = () => {
    setEnabledPages(ALL_PAGE_KEYS.filter((k) => isAlwaysEnabled(k)));
    setSaved(false);
  };

  const save = async () => {
    if (!selectedCompany?.id) return;

    setSaving(true);
    setSaveError(null);

    try {
      await saveEnabledPages(selectedCompany.id, enabledPages);

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error(err);
      setSaveError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const controllableKeys = CONTROLLABLE_PAGES.flatMap((g) => g.pages).map((p) => p.key);
  const totalPages   = controllableKeys.length;
  const enabledCount = enabledPages.filter((k) => controllableKeys.includes(k)).length;

  const filteredGroups = search
    ? CONTROLLABLE_PAGES.map((g) => ({
        ...g,
        pages: g.pages.filter((p) => p.label.toLowerCase().includes(search.toLowerCase())),
      })).filter((g) => g.pages.length > 0)
    : CONTROLLABLE_PAGES;

  return (
    <div className={styles.root} style={{ display: "flex", flexDirection: "column", width: "100%", minWidth: "100%" }} data-testid="CompanyPagesControl-page">
      <main className={styles.main} style={{ width: "100%", flex: 1, maxWidth: "100%", padding: "24px" }} data-testid="CompanyPagesControl-main">
        {!selectedCompany ? (
          <div className={styles.noSelect} data-testid="CompanyPagesControl-no-company-selected">
            <div className={styles.noSelectIcon}><IconBuilding size={40} stroke={1.5} /></div>
            <h3 className={styles.noSelectTitle}>No company selected</h3>
            <p className={styles.noSelectSub}>Please select a company first.</p>
          </div>
        ) : loading ? (
          <div className={styles.noSelect} data-testid="CompanyPagesControl-loading-state">
            <div className={styles.noSelectIcon}><IconLoader2 size={40} stroke={1.5} className="animate-spin" /></div>
            <h3 className={styles.noSelectTitle}>Loading pages…</h3>
          </div>
        ) : loadError ? (
          <div className={styles.noSelect} data-testid="CompanyPagesControl-load-error-state">
            <div className={styles.noSelectIcon}><IconAlertTriangle size={40} stroke={1.5} /></div>
            <h3 className={styles.noSelectTitle}>{loadError}</h3>
          </div>
        ) : (
          <>
            {/* Top bar */}
            <div className={styles.topBar} data-testid="CompanyPagesControl-top-bar">
              <div className={styles.topBarLeft}>
                <div className={styles.topBarAvatar}>
                  {selectedCompany.name?.split(" ").map((w: string) => w[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div>
                  <h2 className={styles.topBarTitle} data-testid="CompanyPagesControl-company-name">{selectedCompany.name}</h2>
                  <p className={styles.topBarSub} data-testid="CompanyPagesControl-enabled-count">{enabledCount} of {totalPages} pages enabled</p>
                </div>
              </div>

              <div className={styles.topBarRight}>
                <div className={styles.progressWrap}>
                  <div className={styles.progressBg}>
                    <div className={styles.progressFill} style={{ width: `${Math.round((enabledCount / totalPages) * 100)}%` }} />
                  </div>
                  <span className={styles.progressPct} data-testid="CompanyPagesControl-progress-pct">{Math.round((enabledCount / totalPages) * 100)}%</span>
                </div>
                <div className={styles.topBarActions}>
                  <button data-testid="CompanyPagesControl-btn-enable-all" className={styles.btnSecondary} onClick={enableAll}><IconEye size={14} /> All On</button>
                  <button data-testid="CompanyPagesControl-btn-disable-all" className={styles.btnSecondary} onClick={disableAll}><IconEyeOff size={14} /> All Off</button>
                  <button
                    data-testid="CompanyPagesControl-btn-save-changes"
                    className={`${styles.btnSave} ${saved ? styles.btnSaved : ""}`}
                    onClick={save}
                    disabled={saving}
                  >
                    {saving ? (
                      <><IconLoader2 size={14} className="animate-spin" /> Saving…</>
                    ) : saved ? (
                      <><IconCheck size={14} /> Saved!</>
                    ) : (
                      "Save changes"
                    )}
                  </button>
                </div>
              </div>
            </div>

            {saveError && (
              <p style={{ color: "#e03131", fontSize: 13, marginTop: 8 }} data-testid="CompanyPagesControl-save-error">{saveError}</p>
            )}

            {/* Search */}
            <div className={styles.searchRow} data-testid="CompanyPagesControl-search-row">
              <div className={styles.searchWrap}>
                <IconSearch size={15} className={styles.searchIco} />
                <input
                  data-testid="CompanyPagesControl-search-input"
                  className={styles.searchInput}
                  placeholder="Search pages…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Groups */}
            <div className={styles.groups} style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }} data-testid="CompanyPagesControl-groups-container">
              {filteredGroups.map((group) => {
                const allOn  = group.pages.every((p) => enabledPages.includes(p.key));
                const someOn = group.pages.some((p) => enabledPages.includes(p.key));
                return (
                  <div key={group.group} className={`${styles.group} ${styles[`group_${group.color}`]}`} style={{ width: "100%" }} data-testid={`CompanyPagesControl-group-${group.group.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className={styles.groupHead}>
                      <div className={styles.groupLeft}>
                        <div className={`${styles.groupDot} ${styles[`dot_${group.color}`]}`} />
                        <span className={styles.groupTitle} data-testid={`CompanyPagesControl-group-title-${group.group.toLowerCase().replace(/\s+/g, '-')}`}>{group.group}</span>
                        <span className={styles.groupCount} data-testid={`CompanyPagesControl-group-count-${group.group.toLowerCase().replace(/\s+/g, '-')}`}>
                          {group.pages.filter((p) => enabledPages.includes(p.key)).length}/{group.pages.length}
                        </span>
                      </div>
                      <button
                        data-testid={`CompanyPagesControl-group-toggle-${group.group.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`${styles.groupToggle} ${allOn ? styles.groupToggleOn : someOn ? styles.groupToggleMixed : ""}`}
                        onClick={() => toggleGroup(group.pages.map((p) => p.key))}
                      >
                        {allOn ? <><IconToggleRight size={16} /> Disable all</> : <><IconToggleLeft size={16} /> Enable all</>}
                      </button>
                    </div>
                    <div className={styles.pageGrid} data-testid={`CompanyPagesControl-group-grid-${group.group.toLowerCase().replace(/\s+/g, '-')}`}>
                      {group.pages.map((page) => {
                        const on   = enabledPages.includes(page.key);
                        const Icon = page.icon;
                        return (
                          <button
                            data-testid={`CompanyPagesControl-page-card-${page.key}`}
                            key={page.key}
                            className={`${styles.pageCard} ${on ? styles.pageCardOn : styles.pageCardOff}`}
                            onClick={() => toggle(page.key)}
                          >
                            <div className={`${styles.pageIcon} ${on ? styles.pageIconOn : ""}`}><Icon size={18} stroke={2} /></div>
                            <span className={styles.pageLabel} data-testid={`CompanyPagesControl-page-label-${page.key}`}>{page.label}</span>
                            <div className={`${styles.pageToggle} ${on ? styles.pageToggleOn : ""}`} data-testid={`CompanyPagesControl-page-toggle-indicator-${page.key}`}>
                              {on ? <IconCheck size={11} /> : <IconX size={11} />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}